import { Menu, MenuItem } from "@mui/material";
import LightPlusBtn from "components/shared/LightPlus";
import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import type { ChannelData, ChannelParams, ITermFormData } from "containers/TermFormContainer";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";

import { formatTypesForOverridePanel, type FormatTypeOption } from "../../utils";
import ChannelBaseColumnsFields from "../ChannelBaseColumnsFields";
import FormatOverrideHeader from "../FormatOverrideHeader";
import { resolveChannelMilestoneBaseColumn } from "../milestoneBaseColumnUtils";
import MilestonesInput from "../MilestonesInput";

type ChannelFormatOverridesProps = {
    channel: IChannel;
    formatTypesOnWork: FormatTypeOption[];
    chosenChannels: ChannelParams[];
    calculationBaseColumnLabels: ICalculationBaseColumnLabel[];
    mode: "edit" | "new";
    useFormData: Pick<ITermFormData, "setError" | "clearErrors">;
    handleToggleFormatTypeOverride: (channelId: number, formatTypeId: number, enabled: boolean) => void;
    handleChannelUpdates: (newChannel: ChannelParams | Record<string, unknown>) => void;
    handleCalculationBaseColumnChange?: (
        channelId: number,
        calculationBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
    ) => void;
    handleMilestoneColumnChange?: (
        channelId: number,
        milestoneBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
    ) => void;
    /** Unsynced compensation group: pick formats per channel; bases stay on the group. */
    hideBaseColumns?: boolean;
    allowCompensationGroup?: boolean;
};

const ChannelFormatOverrides = ({
    channel,
    formatTypesOnWork,
    chosenChannels,
    calculationBaseColumnLabels,
    mode,
    useFormData,
    handleToggleFormatTypeOverride,
    handleChannelUpdates,
    handleCalculationBaseColumnChange,
    handleMilestoneColumnChange,
    hideBaseColumns = false,
    allowCompensationGroup = false,
}: ChannelFormatOverridesProps) => {
    const [t] = useTranslation();
    const addButtonRef = useRef<HTMLSpanElement>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const overrideByFormatId = useMemo(() => {
        const byId = new Map<number, ChannelParams>();
        for (const row of chosenChannels) {
            if (row.channelId !== channel.id || row.formatTypeId == null) continue;
            byId.set(row.formatTypeId, row);
        }
        return byId;
    }, [channel.id, chosenChannels]);
    const { remaining: remainingFormatTypes, active: activeFormatTypes } = formatTypesForOverridePanel(
        formatTypesOnWork,
        overrideByFormatId.keys(),
    );

    const addFormatOverride = (formatTypeId: number) => {
        handleToggleFormatTypeOverride(channel.id, formatTypeId, true);
        setIsMenuOpen(false);
    };

    const handleAddClick = () => {
        if (remainingFormatTypes.length === 1) {
            addFormatOverride(remainingFormatTypes[0].id);
            return;
        }
        setIsMenuOpen(true);
    };

    const hasExistingOverrides = overrideByFormatId.size > 0;
    if (
        channel.calculationMethod !== CalculationMethod.PROPORTIONAL ||
        (channel.compensationGroupId && !allowCompensationGroup) ||
        (formatTypesOnWork.length === 0 && !hasExistingOverrides)
    ) {
        return null;
    }

    return (
        <div className="channel-format-overrides">
            <div className="channel-format-overrides__intro">
                <div className="channel-format-overrides__header">
                    <span>{t("ConnectTitle.CustomizeByFormatType")}</span>
                    {remainingFormatTypes.length > 0 && (
                        <span ref={addButtonRef} className="channel-format-overrides__add">
                            <LightPlusBtn
                                type="plus"
                                tooltipTitle={t("ConnectTitle.AddFormatOverride")}
                                onClick={handleAddClick}
                            />
                        </span>
                    )}
                    <Menu
                        className="channel-format-overrides-menu"
                        anchorEl={addButtonRef.current}
                        open={isMenuOpen}
                        onClose={() => setIsMenuOpen(false)}
                        elevation={0}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                        {remainingFormatTypes.map((formatType) => (
                            <MenuItem key={formatType.id} onClick={() => addFormatOverride(formatType.id)}>
                                {formatType.name}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>
                <p className="channel-format-overrides__hint">{t("ConnectTitle.CustomizeByFormatTypeHint")}</p>
            </div>
            {activeFormatTypes.map((formatType) => {
                const override = overrideByFormatId.get(formatType.id);
                if (!override) return null;
                return (
                    <div className="channel-format-override" key={formatType.id}>
                        <FormatOverrideHeader
                            name={formatType.name}
                            onRemove={() => handleToggleFormatTypeOverride(channel.id, formatType.id, false)}
                        />
                        {!hideBaseColumns && (
                            <ChannelBaseColumnsFields
                                channel={channel}
                                existingChannel={override}
                                calculationBaseColumnLabels={calculationBaseColumnLabels}
                                onCalculationBaseColumnChange={(channelId, calculationBaseColumn) =>
                                    handleCalculationBaseColumnChange?.(channelId, calculationBaseColumn, formatType.id)
                                }
                                onMilestoneColumnChange={(channelId, milestoneBaseColumn) =>
                                    handleMilestoneColumnChange?.(channelId, milestoneBaseColumn, formatType.id)
                                }
                            />
                        )}
                        <MilestonesInput
                            hasMilestones={!!channel.hasMilestones}
                            channel={channel}
                            milestoneBaseColumn={resolveChannelMilestoneBaseColumn(channel, override)}
                            onChange={(newChannel) =>
                                handleChannelUpdates({
                                    ...newChannel,
                                    formatTypeId: formatType.id,
                                })
                            }
                            propMilestones={override.data as ChannelData[]}
                            mode={mode}
                            useFormData={useFormData}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default ChannelFormatOverrides;
